/**
 * Estabilidade da rota já publicada.
 *
 * Depois de publicada, uma rota só muda por ação do utilizador ou por uma nova
 * geração. Atualizações de tempos de viagem (Routes API) alimentam apenas as
 * horas — nunca podem acrescentar, remover ou reordenar paragens.
 *
 * Módulo puro: sem IO, sem React, sem relógio.
 */

/** Ruído aceitável num troço: abaixo disto não há nova revisão publicada. */
export const LEG_MINUTES_NOISE = 2;
/** Ruído aceitável na distância de um troço (metros). */
export const LEG_METERS_NOISE = 150;

export type LegLike = { minutes?: number; distance?: string };

/** Metros a partir de textos como "1.2 km", "800 m", "0,9 km". */
export function legDistanceMeters(distance: string | undefined | null): number {
  if (!distance) return 0;
  const match = String(distance).replace(",", ".").match(/([\d.]+)\s*(km|m)\b/i);
  if (!match) return 0;
  const value = Number.parseFloat(match[1]);
  if (!Number.isFinite(value)) return 0;
  return match[2].toLowerCase() === "km" ? Math.round(value * 1000) : Math.round(value);
}

/**
 * A nova leitura de durações muda alguma coisa que valha uma revisão? Uma
 * diferença de ±1 minuto é ruído de trânsito, não uma rota diferente.
 */
export function legMinutesAreMaterial(
  prev: number[],
  next: number[],
  thresholdMin: number = LEG_MINUTES_NOISE,
): boolean {
  if (prev.length !== next.length) return true;
  for (let i = 0; i < next.length; i++) {
    const a = Number.isFinite(prev[i]) ? prev[i] : 0;
    const b = Number.isFinite(next[i]) ? next[i] : 0;
    if (Math.abs(a - b) >= thresholdMin) return true;
  }
  return false;
}

/** Mesma pergunta para as pernas completas (duração + distância + extremos). */
export function routeLegsAreMaterial(
  prev: LegLike[],
  next: LegLike[],
  options?: { minutesThreshold?: number; metersThreshold?: number },
): boolean {
  const minutesThreshold = options?.minutesThreshold ?? LEG_MINUTES_NOISE;
  const metersThreshold = options?.metersThreshold ?? LEG_METERS_NOISE;
  if (prev.length !== next.length) return true;
  for (let i = 0; i < next.length; i++) {
    const a = prev[i];
    const b = next[i];
    if (Math.abs((a.minutes ?? 0) - (b.minutes ?? 0)) >= minutesThreshold) return true;
    if (Math.abs(legDistanceMeters(a.distance) - legDistanceMeters(b.distance)) >= metersThreshold) {
      return true;
    }
  }
  return false;
}

/**
 * Chave de COMPOSIÇÃO: tudo o que legitimamente muda o conjunto e a ordem das
 * paragens. Os tempos de viagem NÃO entram aqui — é isso que impedia a rota de
 * assentar (uma variação de 1 min expulsava uma paragem e trazia outra).
 */
export function compositionKey(input: {
  generationId?: string;
  modeRevision?: number;
  mode?: string;
  startTime?: string;
  requestText?: string;
  hotelSignature?: string;
  mealPreference?: string;
  visitMinutes?: number | null;
  stops: Array<{ name: string; day?: number | null }>;
}): string {
  return JSON.stringify({
    g: input.generationId ?? "",
    mr: input.modeRevision ?? 0,
    m: input.mode ?? "",
    s: input.startTime ?? "",
    r: input.requestText ?? "",
    h: input.hotelSignature ?? "",
    p: input.mealPreference ?? "",
    v: input.visitMinutes ?? null,
    stops: input.stops.map((s) => [s.name, Math.trunc(s.day ?? 1)]),
  });
}

export type FrozenComposition = {
  key: string;
  /** Minutos de deslocação por nome de paragem no momento do congelamento. */
  minutesByName: Record<string, number | undefined>;
};

/**
 * Devolve a composição congelada para esta chave. Enquanto a chave não muda,
 * a barreira final recebe sempre os MESMOS minutos — logo produz sempre o
 * mesmo conjunto e a mesma ordem.
 */
export function freezeComposition(
  current: FrozenComposition | null,
  key: string,
  stops: Array<{ name: string; minutes?: number }>,
): FrozenComposition {
  if (current && current.key === key) return current;
  const minutesByName: Record<string, number | undefined> = {};
  for (const s of stops) minutesByName[s.name] = s.minutes;
  return { key, minutesByName };
}

/** Aplica os minutos congelados a uma lista de paragens (entrada da barreira). */
export function withFrozenMinutes<T extends { name: string; minutes?: number }>(
  frozen: FrozenComposition,
  stops: T[],
): T[] {
  return stops.map((s) =>
    s.name in frozen.minutesByName ? ({ ...s, minutes: frozen.minutesByName[s.name] } as T) : s,
  );
}
